// ============================================================
// overlayGeometry.js — text-overlay size, wrap width and resize math
// ------------------------------------------------------------
// The Post Generator / Carousel / Scheduler text overlay is laid out from
// overlayMetrics() in the image's NATURAL pixels by both the canvas bake
// (compositeTextOntoImage) and the live CSS preview (TextOverlayPreviewImage,
// which lays out at natural size and scales the whole layer down). So a size
// or width is only ever turned into pixels here, and the two can't drift.
//
// Two optional per-block fields, both resolution-independent fractions:
//   textScale — font size as a fraction of min(W, H). Set by the corner
//               handles; overrides the S/M/L preset (textSize) while present.
//   textWidth — wrap width as a fraction of W. Set by the side handles.
//               Absent: the automatic width every overlay always had
//               (the image minus a 10% margin each side).
// A block with neither renders exactly as it did before they existed.
// ============================================================

export const OVERLAY_SIZE_PRESETS = { s: 0.042, m: 0.06, l: 0.084 };

// Limits, so a handle can't shrink text to nothing or blow it past the image.
// Font: about half of Small up to about 2.4x Large. Width: a tenth of the
// image up to all of it.
export const TEXT_SCALE_MIN = 0.02;
export const TEXT_SCALE_MAX = 0.2;
export const TEXT_WIDTH_MIN = 0.1;
export const TEXT_WIDTH_MAX = 1;

const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
const round = (v, places) => { const k = 10 ** places; return Math.round(v * k) / k; };
// Widths round UP: rounded down, the width a side drag starts from (the text's
// own widest line) could land a hair under that line and re-wrap it.
const roundUp = (v, places) => { const k = 10 ** places; return Math.ceil(v * k - 1e-9) / k; };

const customFraction = (raw, lo, hi) => {
  if (raw === null || raw === undefined || raw === '') return null;
  const v = Number(raw);
  return Number.isFinite(v) && v > 0 ? clamp(v, lo, hi) : null;
};
// The block's custom font size / wrap width, clamped to the limits — or null
// when it has none (it then uses its preset / the automatic width).
export const customTextScale = (o) => customFraction(o?.textScale, TEXT_SCALE_MIN, TEXT_SCALE_MAX);
export const customTextWidth = (o) => customFraction(o?.textWidth, TEXT_WIDTH_MIN, TEXT_WIDTH_MAX);

// The font size fraction the block is drawn at: custom, else its preset.
export const textScaleOf = (o) => customTextScale(o) ?? (OVERLAY_SIZE_PRESETS[o?.textSize] || 0.06);

// Overlay geometry, in the image's natural pixels. compositeTextOntoImage and
// TextOverlayPreviewImage both lay out from these numbers.
export const overlayMetrics = (overlay, W, H) => {
  const fontSize = Math.round(Math.min(W, H) * textScaleOf(overlay));
  const sidePad  = W * 0.1;
  const width    = customTextWidth(overlay);
  return {
    fontSize,
    sidePad,
    maxWidth: width !== null ? W * width : W - sidePad * 2,
    lineGap:  fontSize * 1.2,
    pad:      H * 0.07,
    pillPadX: fontSize * 0.4,
    pillPadY: fontSize * 0.18,
  };
};

// How far left of its alignment x a line (or box) of width w starts.
export const alignOffset = (align, w) => (align === 'left' ? 0 : align === 'right' ? w : w / 2);

// The box the selection outline and handles sit on, in the block's own
// (unrotated) frame, relative to its anchor (alignment x, vertical centre).
// Automatic width: the text itself (its widest line). Custom width: the wrap
// width, so the side handles show where lines will break — or the widest line
// if one word is wider than that. `pivotX` is the rotation pivot's x (the
// centre of the text, as the canvas turns it); its y is always 0.
export const blockFrame = (block, { widest, blockH }, maxWidth) => {
  const align = block.textAlign || 'center';
  const bw = customTextWidth(block) !== null ? Math.max(maxWidth, widest) : widest;
  const left = -alignOffset(align, bw);
  return {
    left, right: left + bw, top: -blockH / 2, bottom: blockH / 2,
    pivotX: -alignOffset(align, widest) + widest / 2,
  };
};

export const RESIZE_HANDLES = ['nw', 'ne', 'se', 'sw', 'w', 'e'];
const OPPOSITE = { nw: 'se', se: 'nw', ne: 'sw', sw: 'ne', w: 'e', e: 'w' };

// A handle's spot on a frame (blockFrame).
export const handlePoint = (frame, h) => ({
  x: h.includes('w') ? frame.left : h.includes('e') ? frame.right : (frame.left + frame.right) / 2,
  y: h[0] === 'n' ? frame.top : h[0] === 's' ? frame.bottom : 0,
});

// Clockwise by `deg` in y-down coordinates — what canvas rotate() and CSS
// rotate() both do with a positive angle.
const rotate = (x, y, deg) => {
  const t = deg * Math.PI / 180, c = Math.cos(t), s = Math.sin(t);
  return { x: x * c - y * s, y: x * s + y * c };
};

// A pointer movement on screen (CSS px, preview at `scale` screen px per
// natural px) as a movement in the block's own frame, in natural px: undo the
// preview's scale, then the block's rotation.
export const screenDeltaToLocal = (dx, dy, scale, rotationDeg) =>
  rotate(dx / scale, dy / scale, -(rotationDeg || 0));

// Where a frame point lands on the image (natural px) for a block anchored at
// `anchor` and turned `rotationDeg` about its pivot.
export const frameToImage = (anchor, frame, p, rotationDeg) => {
  const r = rotate(p.x - frame.pivotX, p.y, rotationDeg || 0);
  return { x: anchor.x + frame.pivotX + r.x, y: anchor.y + r.y };
};

// One handle drag, start to now. Pure: everything it needs is passed in.
//   block   — the block as the drag started (its fields, rotation included)
//   handle  — 'nw' | 'ne' | 'se' | 'sw' (font size) | 'w' | 'e' (width)
//   anchor  — its anchor at the start, natural px {x, y}
//   local   — the pointer's movement in the block's frame (screenDeltaToLocal)
//   W, H    — the image's natural size
//   measure — (block) => { widest, blockH }, the canvas's own wrap of it
// Corners scale the font, keeping the corner opposite the one dragged where
// it is on the image (as it is rotated). A custom width scales with it so the
// line breaks stay put; the automatic width doesn't change. Sides set the
// wrap width, keeping the opposite side where it is; the font is unchanged.
// Returns the fields to merge into the block — always a freeform x/y, since
// the block has moved — and the new anchor in natural px.
export const resizeOverlayBlock = ({ block, handle, anchor, local, W, H, measure }) => {
  const rotation = Number(block.rotation) || 0;
  const m0 = overlayMetrics(block, W, H);
  const f0 = blockFrame(block, measure(block), m0.maxWidth);
  const opp = OPPOSITE[handle];
  const patch = {};
  if (handle === 'w' || handle === 'e') {
    const bw = f0.right - f0.left;
    const next = bw + (handle === 'e' ? local.x : -local.x);
    patch.textWidth = roundUp(clamp(next, TEXT_WIDTH_MIN * W, TEXT_WIDTH_MAX * W) / W, 5);
  } else {
    const frac = textScaleOf(block);
    const custom = customTextWidth(block) !== null;
    const P = handlePoint(f0, handle), O = handlePoint(f0, opp);
    const v = { x: P.x - O.x, y: P.y - O.y };
    const len2 = v.x * v.x + v.y * v.y;
    // How far along the corner's own diagonal the pointer has gone.
    let s = len2 ? ((v.x + local.x) * v.x + (v.y + local.y) * v.y) / len2 : 1;
    let lo = TEXT_SCALE_MIN / frac, hi = TEXT_SCALE_MAX / frac;
    if (custom) {
      lo = Math.max(lo, (TEXT_WIDTH_MIN * W) / m0.maxWidth);
      hi = Math.min(hi, (TEXT_WIDTH_MAX * W) / m0.maxWidth);
    }
    s = clamp(s, lo, Math.max(lo, hi));
    patch.textScale = round(frac * s, 5);
    if (custom) patch.textWidth = roundUp(clamp((m0.maxWidth * s) / W, TEXT_WIDTH_MIN, TEXT_WIDTH_MAX), 5);
  }
  const next = { ...block, ...patch };
  const f1 = blockFrame(next, measure(next), overlayMetrics(next, W, H).maxWidth);
  // The opposite handle stays put on the image: solve for the anchor that
  // puts it back where it was.
  const fixed = frameToImage(anchor, f0, handlePoint(f0, opp), rotation);
  const p1 = handlePoint(f1, opp);
  const r = rotate(p1.x - f1.pivotX, p1.y, rotation);
  const a = { x: fixed.x - f1.pivotX - r.x, y: fixed.y - r.y };
  return { patch: { ...patch, x: round(a.x / W, 4), y: round(a.y / H, 4) }, anchor: a };
};

// The resize cursor for a handle on a block turned `rotationDeg`.
export const handleCursor = (handle, rotationDeg) => {
  const base = { e: 0, se: 45, sw: 135, w: 180, nw: 225, ne: 315 }[handle] || 0;
  const a = (((base + (rotationDeg || 0)) % 180) + 180) % 180;
  const i = Math.round(a / 45) % 4;
  return ['ew-resize', 'nwse-resize', 'ns-resize', 'nesw-resize'][i];
};
